import type Phaser from "phaser";
import type { EffectsDetail } from "daedalus-renderer-phaser";
import type { CharacterFigure } from "../character.ts";
import { RIPPLE_FRAMES, RIPPLE_KEY, type PuddleView } from "./puddle-view.ts";

/**
 * What stands in a puddle looks like it does (P2.27-T20, a look pass the author chooses between): mode A rings the water at
 * the feet with each step taken through a puddle, in the style of the rain's ripples; mode B adds a faint, short reflection
 * of the character in the water under the feet, flipped, low in alpha and shown only where it lies whole in the puddle.
 * Everything is pooled: a splash is one of a few images that play the ripple frames, a reflection is one image per character
 * that has stood in water, drawn from the same texture and frame the character's silhouette is (`CharacterFigure.shapeKey`),
 * so it carries what the character wears; a frame allocates nothing.
 */

/**
 * Which of the effects is on: none, rings at the feet (A), rings and a reflection (B), or by the Effects setting (`auto`: B at
 * Full, A at Reduced, none at Off), which is what the game runs.
 */
export type FeetMode = "auto" | "off" | "a" | "b";

/**
 * Reads `?puddlefx=` of a development page, for comparing the effects.
 *
 * @param raw The parameter's value, or null.
 * @returns `off`, `a` or `b` as named, else `auto`.
 */
export function parseFeetMode(raw: string | null): FeetMode {
  return raw === "off" || raw === "a" || raw === "b" ? raw : "auto";
}

/**
 * The effect in force for a mode at an effects detail.
 *
 * @param mode The mode.
 * @param detail The effects detail.
 * @returns `off`, `a` or `b`.
 */
export function feetEffect(mode: FeetMode, detail: EffectsDetail): "off" | "a" | "b" {
  if (mode !== "auto") return mode;
  return detail === "full" ? "b" : detail === "reduced" ? "a" : "off";
}

/**
 * Calls back for every figure drawn: an id that stays with it and the figure.
 */
export type FigureVisit = (id: number, figure: CharacterFigure) => void;

/**
 * What the feet effects read.
 */
export interface PuddleFeetOptions {
  readonly scene: Phaser.Scene;
  readonly puddles: PuddleView;
  readonly mode: FeetMode;
  readonly figures: (visit: FigureVisit) => void;
}

const STEP_PX = 14;
const JUMP_PX = 96;
const SPLASH_MS = 440;
const SPLASH_POOL = 14;
const SPLASH_SCALES: readonly number[] = [1.2, 0.6];
const RING_HALF_W = 12;
const RING_HALF_H = 6;
const REFLECT_DEPTH_STEP = 0.00003;
const SPLASH_DEPTH_STEP = 0.00002;
const REFLECT_ALPHA = 0.3;
const REFLECT_TINT = 0xb4cce6;
const REFLECT_DEPTHS: readonly number[] = [16, 11, 6];
const REFLECT_SPREAD = 6;

/**
 * What the effects know of one figure.
 */
interface Track {
  x: number;
  y: number;
  walked: number;
  seen: boolean;
  mirror: Phaser.GameObjects.Image | null;
  shown: boolean;
}

/**
 * One splash ring.
 */
interface Splash {
  readonly image: Phaser.GameObjects.Image;
  startedAt: number;
  live: boolean;
}

/**
 * The effects at the feet of every figure standing in a puddle.
 */
export class PuddleFeet {
  private readonly scene: Phaser.Scene;
  private readonly puddles: PuddleView;
  private readonly mode: FeetMode;
  private readonly figures: (visit: FigureVisit) => void;
  private readonly tracks = new Map<number, Track>();
  private readonly splashes: Splash[] = [];
  private next = 0;
  private now = 0;
  private detail: EffectsDetail = "full";
  private effect: "off" | "a" | "b" = "off";

  /**
   * @param options The scene, the puddles, the mode and where the figures come from.
   */
  public constructor(options: PuddleFeetOptions) {
    this.scene = options.scene;
    this.puddles = options.puddles;
    this.mode = options.mode;
    this.figures = options.figures;
  }

  /**
   * Plays a frame: visits every figure, rings the water at the feet of those that stepped in it, shows or hides each
   * reflection, retires the figures no longer drawn and ends the splashes that have played out.
   *
   * @param now The time in milliseconds.
   * @param detail The effects detail: Off draws no splashes, Reduced halves them.
   */
  public update(now: number, detail: EffectsDetail): void {
    this.effect = feetEffect(this.mode, detail);
    if (this.effect === "off") {
      this.tracks.forEach(this.hide);
      this.hideSplashes();
      return;
    }
    this.now = now;
    this.detail = detail;
    this.tracks.forEach(this.unmark);
    this.figures(this.visit);
    this.tracks.forEach(this.sweep);
    for (let i = 0; i < this.splashes.length; i += 1) {
      const splash = this.splashes[i] as Splash;
      if (!splash.live) continue;
      const age = now - splash.startedAt;
      if (age >= SPLASH_MS) {
        splash.live = false;
        splash.image.setVisible(false);
      } else {
        splash.image.setFrame(Math.min(RIPPLE_FRAMES - 1, Math.floor((age / SPLASH_MS) * RIPPLE_FRAMES)));
      }
    }
  }

  /**
   * Destroys the images.
   */
  public destroy(): void {
    this.tracks.forEach((track) => track.mirror?.destroy());
    this.tracks.clear();
    for (const splash of this.splashes) splash.image.destroy();
    this.splashes.length = 0;
  }

  /**
   * Hides a track's reflection.
   *
   * @param track The track.
   */
  private readonly hide = (track: Track): void => {
    if (track.shown) {
      track.mirror?.setVisible(false);
      track.shown = false;
    }
  };

  /**
   * Ends every splash.
   */
  private hideSplashes(): void {
    for (let i = 0; i < this.splashes.length; i += 1) {
      const splash = this.splashes[i] as Splash;
      if (splash.live) {
        splash.live = false;
        splash.image.setVisible(false);
      }
    }
  }

  /**
   * Marks a track unseen at the start of a frame.
   *
   * @param track The track.
   */
  private readonly unmark = (track: Track): void => {
    track.seen = false;
  };

  /**
   * Retires a track whose figure was not drawn this frame.
   *
   * @param track The track.
   * @param id Its figure's id.
   */
  private readonly sweep = (track: Track, id: number): void => {
    if (track.seen) return;
    track.mirror?.destroy();
    this.tracks.delete(id);
  };

  /**
   * Visits one figure: follows it, rings the water as it walks and shows or hides its reflection.
   *
   * @param id The figure's id.
   * @param figure The figure.
   */
  private readonly visit: FigureVisit = (id, figure) => {
    let track = this.tracks.get(id);
    if (track === undefined) {
      track = { x: figure.x, y: figure.y, walked: 0, seen: true, mirror: null, shown: false };
      this.tracks.set(id, track);
    }
    track.seen = true;
    const moved = Math.hypot(figure.x - track.x, figure.y - track.y);
    track.x = figure.x;
    track.y = figure.y;
    const water = this.puddles.waterAt(figure.x, figure.y);
    if (moved > JUMP_PX) track.walked = 0;
    else if (water && this.effect !== "off") {
      track.walked += moved;
      const step = this.detail === "reduced" ? STEP_PX * 2 : STEP_PX;
      if (track.walked >= step) {
        track.walked = 0;
        this.splash(figure.x, figure.y);
      }
    }
    if (this.effect === "b") this.reflect(track, figure, water);
    else this.hide(track);
  };

  /**
   * Starts a ring at a point, in the next place of the pool, at the largest of the ring sizes whose four extremes lie in the water; no
 * ring where even the smallest would spill onto dry ground.
   *
   * @param x World x.
   * @param y World y.
   */
  private splash(x: number, y: number): void {
    let scale = 0;
    for (let i = 0; i < SPLASH_SCALES.length; i += 1) {
      const k = SPLASH_SCALES[i] as number;
      const rx = RING_HALF_W * k;
      const ry = RING_HALF_H * k;
      if (this.puddles.waterAt(x - rx, y - 1) && this.puddles.waterAt(x + rx, y - 1) && this.puddles.waterAt(x, y - 1 - ry) && this.puddles.waterAt(x, y - 1 + ry)) {
        scale = k;
        break;
      }
    }
    if (scale === 0) return;
    let splash = this.splashes[this.next];
    if (splash === undefined) {
      if (!this.scene.textures.exists(RIPPLE_KEY)) return;
      const image = this.scene.add.image(0, 0, RIPPLE_KEY, 0).setDepth(this.puddles.groundDepth + SPLASH_DEPTH_STEP).setVisible(false);
      splash = { image, startedAt: 0, live: false };
      this.splashes.push(splash);
    }
    this.next = (this.next + 1) % SPLASH_POOL;
    splash.startedAt = this.now;
    splash.live = true;
    splash.image.setScale(scale).setFrame(0).setPosition(x, y - 1).setVisible(true);
  }

  /**
   * Shows the reflection of a figure under its feet, as tall as lies whole in the water, or hides it.
   *
   * @param track The figure's track.
   * @param figure The figure.
   * @param water Whether its feet are in water.
   */
  private reflect(track: Track, figure: CharacterFigure, water: boolean): void {
    let height = 0;
    if (water && figure.shapeKey !== "" && figure.lift === 0) {
      const puddles = this.puddles;
      const x = figure.x;
      const y = figure.y;
      for (let i = 0; i < REFLECT_DEPTHS.length; i += 1) {
        const h = REFLECT_DEPTHS[i] as number;
        if (puddles.waterAt(x, y + h) && puddles.waterAt(x - REFLECT_SPREAD, y + h) && puddles.waterAt(x + REFLECT_SPREAD, y + h) && puddles.waterAt(x - REFLECT_SPREAD, y + 2) && puddles.waterAt(x + REFLECT_SPREAD, y + 2)) {
          height = h;
          break;
        }
      }
    }
    if (height === 0) {
      if (track.shown) {
        track.mirror?.setVisible(false);
        track.shown = false;
      }
      return;
    }
    let mirror = track.mirror;
    if (mirror === null) {
      mirror = this.scene.add.image(0, 0, figure.shapeKey, figure.shapeFrame).setFlipY(true).setAlpha(REFLECT_ALPHA).setTint(REFLECT_TINT).setDepth(this.puddles.groundDepth + REFLECT_DEPTH_STEP);
      track.mirror = mirror;
    }
    mirror.setTexture(figure.shapeKey, figure.shapeFrame);
    mirror.setOrigin(figure.originX, figure.originY);
    const frame = mirror.frame;
    mirror.setCrop(0, frame.height * figure.originY - height, frame.width, height);
    mirror.setPosition(figure.x, figure.y);
    if (!track.shown) {
      mirror.setVisible(true);
      track.shown = true;
    }
  }
}
